import React, { useState, useEffect } from 'react';
import {
  TextField,
  Button,
  Grid,
  Typography,
  Tooltip,
  IconButton,
  Container,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemText
} from '@material-ui/core';
import RefreshIcon from '@material-ui/icons/Refresh';
import { Info as InfoIcon, Lock as LockIcon, CloudUpload as UploadIcon } from '@material-ui/icons';
import { useParams } from 'react-router-dom';
import { makeStyles, createMuiTheme, ThemeProvider } from '@material-ui/core/styles';
import axios from 'axios'; // Switched to plain axios since no auth needed
import invoiageLogo from "../../../images/branding/invoiage/invoiage-transparent-logo-small.png";
import EPLogo from "../../../images/branding/logo.png"
import orange from '@material-ui/core/colors/orange';

const isInvoiage = process.env.REACT_APP_MODE == 'invoiage'

// Define custom styles using makeStyles
const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e9f0 100%)', 
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(2),
  },
  logo: {
    display: 'block',
    margin: '0 auto 20px',
    maxWidth: '200px',
    height: 'auto',
  },
  container: {
    [theme.breakpoints.up('sm')]: {
      marginBottom: theme.spacing(8),
    },
  },
  paper: {
    padding: theme.spacing(4),
    borderRadius: 12,
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
    backgroundColor: '#ffffff',
    maxWidth: 600,
    margin: '0 auto',
  },
  header: {
    '&.MuiTypography-root': {
      marginTop: theme.spacing(1),
      color: isInvoiage ? '#1a237e' : 'black',
      marginBottom: theme.spacing(1),
    }
  },
  subheader: {
    '&.MuiTypography-root': {
      color: '#455a64', // Softer gray for secondary text
      lineHeight: 1.5,
    }
  },
  divider: {
    '&.MuiDivider-root': {
      margin: theme.spacing(2, 0),
      backgroundColor: '#e0e0e0',
      border: 'none', // Remove default border
      height: 1, // Ensure divider is visible
    },
  },
  button: {
    '&.MuiButton-root': {
      padding: theme.spacing(1.5),
      fontWeight: 500,
      color: 'white',
      textTransform: 'none', // Less aggressive button text
      backgroundColor: isInvoiage ? '#001357' : orange[700], // Brand blue
      '&:hover': {
        backgroundColor: isInvoiage ? '#001045' : orange[900], // Darker blue on hover
      },
    }
  },
  lockIcon: {
    color: '#757575', // Neutral gray for the lock icon
  },
  fileList: {
    maxHeight: 200,
    overflow: 'auto',
    border: '1px solid #e0e0e0',
    borderRadius: 4,
    marginTop: theme.spacing(1),
  },
}));

const FileUploadForm = () => {
  const classes = useStyles();

  const customTheme = createMuiTheme({
    palette: {
      primary: {
        main: isInvoiage ? '#001357' : orange[600], // Change this to your desired primary color
      },
    },
  });

  const { uuid } = useParams();

  // State for files and submission status
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [valid, setValid] = useState(false);

  // Ref for hidden file input
  const fileInputRef = React.useRef(null);

  const resetForm = () => {
    setFiles([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    setLoading(false);
    setError(null);
    setSuccess(false);
  };

  useEffect(() => {
    setValid(files.length > 0);
  }, [files]);

  // Handle file selection
  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setFiles(selectedFiles);
    setError(null); // Clear any previous errors
  };

  // Trigger file input click
  const handleChooseFiles = () => {
    fileInputRef.current.click();
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);

    // Basic validation
    if (files.length === 0) {
      setError('Please select at least one file to upload.');
      setLoading(false);
      return;
    }

    // Prepare FormData for multipart upload
    const formData = new FormData();
    formData.append('personalizedCode', uuid);
    files.forEach((file) => {
      formData.append('files', file); // Append each file; server can handle multiple under 'files'
    });

    try {
      await axios.post(`${process.env.REACT_APP_EP_API_URL}/public/upload/${uuid}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setSuccess(true);
    } catch (err) {
      setError('An error occurred while uploading your files. Please try again.');
      console.error(err)
    } finally {
      setLoading(false);
    }
  };

  return (
    <ThemeProvider theme={customTheme}>
      <div className={classes.root}>
        <Container maxWidth="sm" className={classes.container}>
          <Paper className={classes.paper}>
            {/* Company Logo */}
            <img src={isInvoiage ? invoiageLogo : EPLogo} alt="Invoiage Logo" className={classes.logo} />
            <Divider className={classes.divider} />
            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                {/* Form Header */}
                <Grid item xs={12}>
                  <Typography variant="h5" align="center" className={classes.header}>
                    Utility Bill File Upload
                  </Typography>
                  <Typography variant="body2" align="center" className={classes.subheader}>
                    Help us process your utility bills by uploading them below.
                    Your files are handled securely.
                  </Typography>
                </Grid>

                {/* File Uploader */}
                <Grid item xs={12}>
                  
                  <Grid container spacing={1} alignItems="center">
                    <Grid item xs={11}>
                      <input
                        type="file"
                        multiple
                        ref={fileInputRef}
                        style={{ display: 'none' }}
                        onChange={handleFileChange}
                      />
                      <Button
                        variant="outlined"
                        color="primary"
                        onClick={handleChooseFiles}
                        fullWidth
                        startIcon={<UploadIcon />}
                      >
                        Choose Files to Upload
                      </Button>
                    </Grid>
                    <Grid item xs={1}>
                      <Tooltip title="Upload your utility bill files (e.g., PDFs, images). Multiple files allowed. Recommended naming convention: UtilityCompany_AcctNum_InvoiceDate.pdf">
                        <IconButton tabIndex={-1}>
                          <InfoIcon />
                        </IconButton>
                      </Tooltip>
                    </Grid>
                  </Grid>
                  

                  {files.length > 0 && (
                    <List className={classes.fileList}>
                      {files.map((file, index) => (
                        <ListItem key={index}>
                          <ListItemText primary={file.name} secondary={`${(file.size / 1024).toFixed(2)} KB`} />
                        </ListItem>
                      ))}
                    </List>
                  )}

                </Grid>

                {/* Submit Button */}
                <Grid item xs={12}>
                  <Grid container spacing={1} alignItems="center">
                    <Grid item xs={isInvoiage ? 11 : 12}>
                      <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={!valid || loading || success}
                        fullWidth
                        className={classes.button}
                      >
                        {loading ? 'Uploading...' : success ? 'Uploaded' : 'Upload Files'}
                      </Button>
                    </Grid>
                    {isInvoiage &&
                      <Grid item xs={1}>
                        <Tooltip title="Learn how Invoiage keeps your data secure">
                          <a
                            href="https://www.invoiage.ai/security"
                            target="_blank"
                            rel="noopener noreferrer"
                            className={classes.lockIcon}
                          >
                            <IconButton>
                              <LockIcon color="primary" />
                            </IconButton>
                          </a>
                        </Tooltip>
                      </Grid>
                    }
                  </Grid>
                </Grid>

                {/* Feedback Messages */}
                {error && (
                  <Grid item xs={12}>
                    <Typography color="error" variant="body2">
                      {error}
                    </Typography>
                  </Grid>
                )}
                {success && (
                  <Grid item xs={12}>
                    <Typography
                      color="primary"
                      variant="body2"
                      style={{ display: 'flex', justifyContent: 'space-around', alignItems: 'center' }}
                    >
                      Success! Your files have been uploaded.
                      <Button className={classes.button} variant="contained" onClick={resetForm}>
                        <span style={{color: 'white'}}></span>Reset Form <RefreshIcon />
                      </Button>
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </form>
          </Paper>
        </Container>
      </div>
    </ThemeProvider>
  );
};

export default FileUploadForm;